import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useCurrency } from '../contexts/CurrencyContext'
import {
  Box, Typography, IconButton, Divider, Stack, Chip, Tabs, Tab, Tooltip, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DownloadIcon from '@mui/icons-material/Download'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import InventoryIcon from '@mui/icons-material/Inventory'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import FactoryIcon from '@mui/icons-material/Factory'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import TerminalIcon from '@mui/icons-material/Terminal'
import InsightsPanel from './InsightsPanel'
import { generatePDFReport, downloadPDF } from '../utils/pdfGenerator'
import { generateInsights } from '../utils/insightsEngine'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts'

// ── Node type config ────────────────────────────────────────────────
const NODE_TYPE_CONFIG = {
  supplier:     { color: '#10b981', Icon: WarehouseIcon,     label: 'Supplier'     },
  factory:      { color: '#f59e0b', Icon: FactoryIcon,       label: 'Factory'      },
  manufacturer: { color: '#f59e0b', Icon: FactoryIcon,       label: 'Manufacturer' },
  distributor:  { color: '#3b82f6', Icon: LocalShippingIcon, label: 'Distributor'  },
  retailer:     { color: '#8b5cf6', Icon: StorefrontIcon,    label: 'Retailer'     },
}

// ── Formatters ──────────────────────────────────────────────────────
function getFillRateColor(r) {
  if (r == null) return '#94a3b8'
  if (r >= 90) return '#10b981'
  if (r >= 70) return '#f59e0b'
  return '#ef4444'
}
function computeFillRate(stats) {
  if (!stats) return null
  const f = stats.demand_fulfilled?.[1] || 0
  const r = stats.demand_received?.[1]  || 0
  return r > 0 ? Math.round((f / r) * 100) : null
}
function fmtMoney(v, d = 0, sym = '$') {
  if (v == null) return '—'
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `${v < 0 ? '-' : ''}${sym}${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1000)      return `${v < 0 ? '-' : ''}${sym}${(abs / 1000).toFixed(1)}k`
  return `${v < 0 ? '-' : ''}${sym}${abs.toFixed(d)}`
}
function fmtNum(v) {
  if (v == null) return '—'
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k`
  return String(Math.round(v))
}
function formatValue(val, isArray = false) {
  if (isArray && Array.isArray(val)) return `${val[0]} orders, ${val[1].toLocaleString()} units`
  if (typeof val === 'number') return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return val ?? '—'
}

// ── Log parsing ─────────────────────────────────────────────────────
function parseLogLine(raw) {
  // Strip any leading timestamp (e.g. "2024-01-01 12:00:00,000 ")
  const cleaned = raw.replace(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}[,.\d]*\s*/, '')
  const match = cleaned.match(/^(INFO|WARNING|ERROR|DEBUG)\s+(\S+)\s+-\s+(.*)$/)
  if (!match) return null
  const [, level, source, rest] = match
  const timeMatch = rest.match(/^([\d]+\.[\d]+):(.+)$/)
  if (timeMatch) {
    return { level, source, time: parseFloat(timeMatch[1]), message: timeMatch[2].trim(), isSummary: false }
  }
  return { level, source, time: null, message: rest.trim(), isSummary: source === 'sim_trace' }
}

function logEventType(msg) {
  const m = msg.toLowerCase()
  if (m.includes('inventory levels') || m.includes('on hand')) return 'inventory'
  if (m.includes('replenishing') || m.includes('order placed'))  return 'order'
  if (m.includes('shipment') || m.includes('replenished') || m.includes('in transit')) return 'shipment'
  if (m.includes('customer') || m.includes('order quantity'))    return 'demand'
  return 'info'
}

const LOG_COLORS = {
  inventory: '#3b82f6',
  order:     '#8b5cf6',
  shipment:  '#10b981',
  demand:    '#f59e0b',
  info:      '#64748b',
}

const LOG_FILTERS = [
  { key: 'all',       label: 'All'          },
  { key: 'inventory', label: 'Inventory'    },
  { key: 'order',     label: 'Orders'       },
  { key: 'shipment',  label: 'Shipments'    },
  { key: 'demand',    label: 'Demand'       },
  { key: 'summary',   label: 'Summary'      },
]

// ── Simulation Log Viewer ───────────────────────────────────────────
function SimLogViewer({ logs, nodes, demands, isDark, border, textSecondary, textPrimary, accent, surfaceEl }) {
  const [typeFilter, setTypeFilter]  = useState('all')
  const [nodeFilter, setNodeFilter]  = useState('all')

  // Build a map from raw SupplyNetPy source IDs → friendly display names
  const sourceMap = {}
  if (nodes)   nodes.forEach(n => { sourceMap[n.id] = n.data.label })
  if (demands) demands.forEach(d => { sourceMap[d.id] = d.name })

  if (!logs || logs.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <TerminalIcon sx={{ fontSize: 32, color: isDark ? '#334155' : '#cbd5e1', mb: 1 }} />
        <Typography sx={{ fontSize: '13px', color: textSecondary }}>
          No simulation logs — run a simulation first
        </Typography>
      </Box>
    )
  }

  const parsed = logs.map(parseLogLine).filter(Boolean)
  const sources = [...new Set(parsed.filter(l => !l.isSummary).map(l => l.source))].sort()

  const filtered = parsed.filter(entry => {
    if (typeFilter === 'summary') return entry.isSummary
    if (entry.isSummary)         return typeFilter === 'all'
    const type = logEventType(entry.message)
    if (typeFilter !== 'all' && type !== typeFilter) return false
    if (nodeFilter !== 'all' && entry.source !== nodeFilter) return false
    return true
  })

  const pillSx = active => ({
    px: 1.25, py: 0.375, borderRadius: '50px', flexShrink: 0,
    fontSize: '11px', fontWeight: 600, cursor: 'pointer', userSelect: 'none',
    bgcolor: active
      ? (isDark ? 'rgba(99,102,241,0.22)' : 'rgba(99,102,241,0.1)')
      : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
    color: active ? accent : textSecondary,
    border: `1px solid ${active ? (isDark ? 'rgba(99,102,241,0.38)' : 'rgba(99,102,241,0.22)') : 'transparent'}`,
    transition: 'all 0.15s ease',
    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
  })

  return (
    <Box>
      {/* Filter bar — event type + node source in one compact area */}
      <Box sx={{
        display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5,
        flexWrap: 'wrap',
      }}>
        {/* Type pills */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {LOG_FILTERS.map(({ key, label }) => (
            <Box key={key} onClick={() => setTypeFilter(key)} sx={pillSx(typeFilter === key)}>{label}</Box>
          ))}
        </Stack>

        {/* Divider */}
        {sources.length > 1 && (
          <Box sx={{ width: 1, height: 20, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', flexShrink: 0 }} />
        )}

        {/* Node pills — single scrollable row with friendly names */}
        {sources.length > 1 && (
          <Box sx={{
            display: 'flex', gap: 0.5, overflowX: 'auto', flex: 1,
            '&::-webkit-scrollbar': { height: 2 },
            '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)', borderRadius: 2 },
          }}>
            <Box onClick={() => setNodeFilter('all')} sx={pillSx(nodeFilter === 'all')}>All</Box>
            {sources.map(src => (
              <Box key={src} onClick={() => setNodeFilter(src)} sx={{
                ...pillSx(nodeFilter === src),
                maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {sourceMap[src] || src}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Log entries */}
      <Box sx={{
        border: `1px solid ${border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: isDark ? '#060b14' : '#f8fafc',
        maxHeight: 380,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)', borderRadius: 2 },
      }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '12px', color: textSecondary }}>No entries match the current filter</Typography>
          </Box>
        ) : filtered.map((entry, i) => {
          const type = entry.isSummary ? 'info' : logEventType(entry.message)
          const color = entry.level === 'WARNING' ? '#f59e0b' : entry.level === 'ERROR' ? '#ef4444' : LOG_COLORS[type]

          return (
            <Box
              key={i}
              sx={{
                display: 'flex', alignItems: 'baseline', gap: 1.25,
                px: 1.75, py: 0.5,
                borderBottom: i < filtered.length - 1
                  ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`
                  : 'none',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)' },
              }}
            >
              {/* Sim time */}
              <Typography sx={{
                fontSize: '10px', fontWeight: 600, color: isDark ? '#2d3f58' : '#94a3b8',
                minWidth: 32, flexShrink: 0, fontFamily: 'monospace', letterSpacing: '0.02em',
              }}>
                {entry.time != null ? entry.time.toFixed(1) : '—'}
              </Typography>

              {/* Source badge */}
              <Box sx={{
                px: 0.75, borderRadius: '4px', fontSize: '9.5px', fontWeight: 800,
                bgcolor: `${color}1a`, color, minWidth: 28, textAlign: 'center',
                flexShrink: 0, fontFamily: 'monospace', letterSpacing: '0.03em',
              }}>
                {entry.source}
              </Box>

              {/* Message */}
              <Typography sx={{
                fontSize: '11.5px', lineHeight: 1.6, flex: 1, wordBreak: 'break-word',
                fontFamily: entry.isSummary ? 'monospace' : 'inherit',
                color: entry.isSummary ? accent
                  : entry.level === 'WARNING' ? '#f59e0b'
                  : entry.level === 'ERROR'   ? '#ef4444'
                  : (isDark ? '#64748b' : '#475569'),
              }}>
                {entry.message}
              </Typography>
            </Box>
          )
        })}
      </Box>

      <Typography sx={{ fontSize: '11px', color: isDark ? '#2d3f58' : '#cbd5e1', mt: 0.875, textAlign: 'right' }}>
        {filtered.length}{typeFilter !== 'all' || nodeFilter !== 'all' ? ` / ${parsed.length}` : ''} entries
      </Typography>
    </Box>
  )
}

// ── Main component ──────────────────────────────────────────────────
function SimulationDashboard({ results, nodes, edges, demands, simTime, onClose, onExport, initialNodeId }) {
  const theme  = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { currency } = useCurrency()
  const sym = currency.symbol
  const [expanded, setExpanded]         = useState(true)
  const [panelHeight, setPanelHeight]   = useState(window.innerHeight * 0.65)
  const isResizing                      = useRef(false)
  const [tabValue, setTabValue]         = useState(0)
  const [selectedNodeId, setSelectedNodeId] = useState('network')

  useEffect(() => {
    if (initialNodeId && initialNodeId !== 'network') {
      setSelectedNodeId(initialNodeId)
      setExpanded(true)
    }
  }, [initialNodeId])

  const handleResizeStart = useCallback(e => {
    e.preventDefault(); e.stopPropagation()
    isResizing.current = true
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
    const onMove = ev => {
      if (!isResizing.current) return
      setPanelHeight(Math.max(120, Math.min(window.innerHeight - ev.clientY, window.innerHeight * 0.92)))
    }
    const onUp = () => {
      isResizing.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup',   onUp)
  }, [])

  if (!results || !results.success) return null

  const { metrics, inventory_data, individual_stats } = results
  const simulationLogs = results.simulation_logs || []

  // Build chart data
  const chartData = {}
  if (inventory_data) {
    Object.keys(inventory_data).forEach(nodeId => {
      const d = inventory_data[nodeId]
      const name = nodes.find(n => n.id === nodeId)?.data.label || nodeId
      if (d?.time && d?.level)
        chartData[nodeId] = d.time.map((t, i) => ({ time: Math.round(t), [name]: d.level[i] }))
    })
  }

  const handleExportPDF = () => {
    try {
      if (!results || !nodes || !edges || !demands) { alert('Missing required data'); return }
      const insights = generateInsights(results, nodes, edges, demands)
      const doc = generatePDFReport(results, { nodes, links: edges, demands, sim_time: simTime || 30 }, insights)
      downloadPDF(doc, 'supply-chain-simulation-report')
    } catch (err) {
      console.error('PDF error:', err)
      alert(`Failed to generate PDF: ${err.message}`)
    }
  }

  const getCurrentStats = () =>
    selectedNodeId === 'network' ? metrics : individual_stats?.[selectedNodeId]?.stats || null

  const currentStats = getCurrentStats()
  const isNodeView   = selectedNodeId !== 'network'

  const sortedNodes = individual_stats
    ? Object.entries(individual_stats).sort(([, a], [, b]) => {
        const o = { supplier: 1, factory: 2, manufacturer: 2, distributor: 3, retailer: 4 }
        return (o[a.type] || 5) - (o[b.type] || 5)
      })
    : []

  const kpiProfit = isNodeView ? currentStats?.profit        ?? null : metrics.profit        ?? null
  const kpiCost   = isNodeView ? currentStats?.node_cost     ?? null : metrics.total_cost    ?? null
  const kpiInv    = isNodeView ? currentStats?.inventory_level ?? null : metrics.available_inv ?? null
  const kpiShort  = isNodeView ? currentStats?.orders_shortage?.[1] ?? 0 : metrics.shortage?.[1] ?? 0

  // ── Design tokens ────────────────────────────────────────────────
  const surface    = isDark ? '#0d1626' : '#eef2f7'
  const surfaceEl  = isDark ? '#1a2a40' : '#ffffff'
  const sidebarBg  = isDark ? '#0a111e' : '#e3e9f2'
  const border     = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.09)'
  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#64748b'
  const accent     = isDark ? '#818cf8' : '#4f46e5'

  // ── Inner: KPI card ─────────────────────────────────────────────
  const KPICard = ({ title, value, subtitle, icon: Icon, color, profitSign }) => (
    <Box sx={{
      p: 2, borderRadius: '12px', border: `1px solid ${border}`, bgcolor: surfaceEl,
      position: 'relative', overflow: 'hidden',
      '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2.5px', background: color },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary }}>
          {title}
        </Typography>
        <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon sx={{ fontSize: 14, color }} />
        </Box>
      </Stack>
      <Typography sx={{
        fontSize: '22px', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: profitSign === true ? '#10b981' : profitSign === false ? '#ef4444' : textPrimary,
      }}>
        {value}
      </Typography>
      {subtitle && <Typography sx={{ fontSize: '11px', color: textSecondary, mt: 0.4 }}>{subtitle}</Typography>}
    </Box>
  )

  // ── Inner: stat tile grid ────────────────────────────────────────
  const StatGrid = ({ items }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
      {items.map(({ label, value, color, sub }) => (
        <Box key={label} sx={{
          p: 1.5, borderRadius: '10px',
          bgcolor: surfaceEl,
          border: `1px solid ${border}`,
          borderLeft: `3px solid ${color}`,
          display: 'flex', flexDirection: 'column', gap: 0.25,
          transition: 'transform 0.12s ease, box-shadow 0.12s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: isDark ? `0 4px 16px rgba(0,0,0,0.25)` : `0 4px 16px rgba(0,0,0,0.06)`,
          },
        }}>
          <Typography sx={{
            fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: textSecondary, lineHeight: 1,
          }}>
            {label}
          </Typography>
          <Typography sx={{
            fontSize: '16px', fontWeight: 800, color: textPrimary,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.2,
            letterSpacing: '-0.01em',
          }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: '10px', color: textSecondary, lineHeight: 1 }}>
              {sub}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  )

  // ── Inner: node sidebar item ─────────────────────────────────────
  const NodeSidebarItem = ({ id, name, type, selected, onSelect, fillRate, isNetwork }) => {
    const typeConf = NODE_TYPE_CONFIG[type] || { color: '#64748b', Icon: AccountTreeIcon }
    const { color, Icon: TypeIcon } = typeConf
    return (
      <Box
        onClick={onSelect}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1, py: 0.75, cursor: 'pointer', borderRadius: '8px', mb: 0.25,
          bgcolor: selected ? (isDark ? 'rgba(99,102,241,0.14)' : 'rgba(99,102,241,0.08)') : 'transparent',
          border: `1px solid ${selected ? (isDark ? 'rgba(99,102,241,0.28)' : 'rgba(99,102,241,0.18)') : 'transparent'}`,
          transition: 'all 0.15s ease',
          '&:hover': {
            bgcolor: selected
              ? (isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.12)')
              : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
          },
        }}
      >
        <Box sx={{
          width: 26, height: 26, borderRadius: '7px', flexShrink: 0,
          bgcolor: isNetwork ? (isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)') : color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isNetwork ? 1 : 0.88,
        }}>
          {isNetwork
            ? <AccountTreeIcon sx={{ fontSize: 13, color: isDark ? '#818cf8' : '#4f46e5' }} />
            : <TypeIcon sx={{ fontSize: 13, color: '#fff' }} />
          }
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '12px', fontWeight: 600, lineHeight: 1.2,
            color: selected ? (isDark ? '#a5b4fc' : '#4338ca') : (isDark ? '#e2e8f0' : '#1e293b'),
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {name}
          </Typography>
          {!isNetwork && type && (
            <Typography sx={{ fontSize: '10px', color: textSecondary, textTransform: 'capitalize', lineHeight: 1 }}>
              {type}
            </Typography>
          )}
        </Box>
        {!isNetwork && (
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            bgcolor: getFillRateColor(fillRate),
            boxShadow: `0 0 5px ${getFillRateColor(fillRate)}70`,
          }} />
        )}
      </Box>
    )
  }

  // ── Inner: area chart panel ──────────────────────────────────────
  const ChartPanel = ({ nodeId }) => {
    const nodeData  = individual_stats?.[nodeId]
    const nodeName  = nodes.find(n => n.id === nodeId)?.data.label || nodeId
    const nodeType  = nodeData?.type || 'supplier'
    const typeColor = NODE_TYPE_CONFIG[nodeType]?.color || '#6366f1'
    const data      = chartData[nodeId]
    if (!data) return null
    return (
      <Box sx={{ p: 2, borderRadius: '12px', border: `1px solid ${border}`, bgcolor: surfaceEl }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: typeColor }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: textPrimary }}>{nodeName}</Typography>
          <Typography sx={{ fontSize: '11px', color: textSecondary, textTransform: 'capitalize' }}>{nodeData?.type}</Typography>
        </Stack>
        <ResponsiveContainer width="100%" height={isNodeView ? 240 : 160}>
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 16, left: 0 }}>
            <defs>
              <linearGradient id={`grad-${nodeId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={typeColor} stopOpacity={0.25} />
                <stop offset="95%" stopColor={typeColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} />
            <XAxis dataKey="time" tick={{ fontSize: 10, fill: isDark ? '#475569' : '#94a3b8' }} axisLine={false} tickLine={false}
              label={{ value: 'Days', position: 'insideBottom', offset: -4, style: { fontSize: 10, fill: isDark ? '#475569' : '#94a3b8' } }} />
            <YAxis tick={{ fontSize: 10, fill: isDark ? '#475569' : '#94a3b8' }} axisLine={false} tickLine={false}
              label={{ value: 'Units', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: isDark ? '#475569' : '#94a3b8' } }} />
            <RechartsTooltip contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#fff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', fontSize: '12px',
              color: isDark ? '#e2e8f0' : '#1e293b',
            }} labelStyle={{ color: isDark ? '#94a3b8' : '#6b7280', fontWeight: 600 }} />
            <Area type="monotone" dataKey={nodeName} stroke={typeColor} strokeWidth={2}
              fill={`url(#grad-${nodeId})`} dot={false} activeDot={{ r: 4, fill: typeColor, strokeWidth: 0 }} />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    )
  }

  // ── COLLAPSED: floating pill ─────────────────────────────────────
  if (!expanded) {
    return (
      <Box sx={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
        display: 'flex', alignItems: 'center', gap: 0.5,
        pl: 1.75, pr: 0.75, py: 0.75, borderRadius: '50px',
        background: isDark
          ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
          : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
        color: '#fff',
        boxShadow: '0 4px 24px rgba(79,70,229,0.45), 0 2px 8px rgba(0,0,0,0.2)',
        backdropFilter: 'blur(12px)',
        userSelect: 'none',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: '0 8px 32px rgba(79,70,229,0.55), 0 4px 12px rgba(0,0,0,0.25)', transform: 'translateY(-2px)' },
      }}>
        <Box onClick={() => setExpanded(true)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
          <ShowChartIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '13px', letterSpacing: '0.01em', whiteSpace: 'nowrap', lineHeight: 1 }}>
            Simulation Results
          </Typography>
          <Chip
            label={`${sortedNodes.length} Nodes`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, height: 18, fontSize: '10.5px', '& .MuiChip-label': { px: 0.75 } }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Export PDF" placement="top" arrow>
            <IconButton size="small" onClick={e => { e.stopPropagation(); handleExportPDF() }}
              sx={{ color: 'rgba(255,255,255,0.75)', width: 26, height: 26, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <PictureAsPdfIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export CSV" placement="top" arrow>
            <IconButton size="small" onClick={e => { e.stopPropagation(); onExport() }}
              sx={{ color: 'rgba(255,255,255,0.75)', width: 26, height: 26, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <DownloadIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Expand" placement="top" arrow>
            <IconButton size="small" onClick={() => setExpanded(true)}
              sx={{ color: 'rgba(255,255,255,0.75)', width: 26, height: 26, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <ExpandLessIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close" placement="top" arrow>
            <IconButton size="small" onClick={e => { e.stopPropagation(); onClose() }}
              sx={{ color: 'rgba(255,255,255,0.65)', width: 26, height: 26, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    )
  }

  // ── EXPANDED panel ───────────────────────────────────────────────
  return (
    <Box sx={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      height: panelHeight, overflow: 'hidden', zIndex: 1000,
      display: 'flex', flexDirection: 'column',
      bgcolor: surface,
      borderTop: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.18)'}`,
      boxShadow: isDark ? '0 -12px 48px rgba(0,0,0,0.5)' : '0 -8px 32px rgba(0,0,0,0.1)',
    }}>

      {/* Drag handle */}
      <Box onMouseDown={handleResizeStart} sx={{
        height: 20, cursor: 'row-resize', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover .dp': { width: 48, opacity: 1 },
      }}>
        <Box className="dp" sx={{
          height: 3, width: 36, borderRadius: 2, transition: 'all 0.2s ease', opacity: 0.65,
          bgcolor: isDark ? 'rgba(99,102,241,0.45)' : 'rgba(99,102,241,0.35)',
        }} />
      </Box>

      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 2.5, py: 1, flexShrink: 0,
        bgcolor: isDark ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.04)',
        borderBottom: `1px solid ${border}`,
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: '9px',
            bgcolor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShowChartIcon sx={{ fontSize: 16, color: accent }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: textPrimary, letterSpacing: '-0.01em' }}>
            Simulation Results
          </Typography>
          <Chip label={`${simTime || 30} days`} size="small"
            sx={{ height: 20, fontSize: '11px', bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: textSecondary, fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }} />
          <Chip label={`${sortedNodes.length} nodes`} size="small"
            sx={{ height: 20, fontSize: '11px', bgcolor: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)', color: accent, fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }} />
          {metrics.profit != null && (
            <Chip
              label={`${metrics.profit >= 0 ? '+' : ''}${fmtMoney(metrics.profit, 0, sym)} net`}
              size="small"
              sx={{
                height: 20, fontSize: '11px', fontWeight: 700, '& .MuiChip-label': { px: 0.75 },
                bgcolor: metrics.profit >= 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                color:   metrics.profit >= 0 ? '#10b981' : '#ef4444',
              }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title="Export PDF" placement="bottom" arrow>
            <IconButton size="small" onClick={handleExportPDF}
              sx={{ color: textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: textPrimary } }}>
              <PictureAsPdfIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export CSV" placement="bottom" arrow>
            <IconButton size="small" onClick={onExport}
              sx={{ color: textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: textPrimary } }}>
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75, opacity: 0.4 }} />
          <Tooltip title="Minimise" placement="bottom" arrow>
            <IconButton size="small" onClick={() => setExpanded(false)}
              sx={{ color: textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: textPrimary } }}>
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close" placement="bottom" arrow>
            <IconButton size="small" onClick={onClose}
              sx={{ color: textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', color: '#ef4444' } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Body: sidebar + main content */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box sx={{ width: 204, flexShrink: 0, overflow: 'auto', borderRight: `1px solid ${border}`, bgcolor: sidebarBg, p: 1.25 }}>
          <NodeSidebarItem id="network" name="Overall Network" type="network"
            selected={selectedNodeId === 'network'} onSelect={() => setSelectedNodeId('network')}
            fillRate={null} isNetwork={true} />
          {sortedNodes.length > 0 && (
            <>
              <Typography sx={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? '#334155' : '#cbd5e1', px: 1, pt: 1.25, pb: 0.5 }}>
                Individual Nodes
              </Typography>
              {sortedNodes.map(([nodeId, nodeData]) => (
                <NodeSidebarItem key={nodeId} id={nodeId} name={nodeData.name} type={nodeData.type}
                  selected={selectedNodeId === nodeId}
                  onSelect={() => { setSelectedNodeId(nodeId); setTabValue(0) }}
                  fillRate={computeFillRate(nodeData.stats)} isNetwork={false} />
              ))}
            </>
          )}
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 2 }}>

          {/* KPI cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
            <KPICard title={isNodeView ? 'Profit' : 'Net Profit'} value={fmtMoney(kpiProfit, 0, sym)}
              subtitle={isNodeView ? `Revenue: ${fmtMoney(currentStats?.revenue, 0, sym)}` : `Revenue: ${fmtMoney(metrics.revenue, 0, sym)}`}
              icon={TrendingUpIcon} color="#10b981"
              profitSign={kpiProfit != null ? kpiProfit >= 0 : undefined} />
            <KPICard title={isNodeView ? 'Node Cost' : 'Total Cost'} value={fmtMoney(kpiCost, 0, sym)}
              subtitle={isNodeView ? `Holding: ${fmtMoney(currentStats?.inventory_carry_cost, 0, sym)}` : `Avg/item: ${sym}${(metrics.avg_cost_per_item || 0).toFixed(3)}`}
              icon={AttachMoneyIcon} color="#3b82f6" />
            <KPICard title="Inventory" value={fmtNum(kpiInv)}
              subtitle={isNodeView ? `Waste: ${fmtNum(currentStats?.inventory_waste)} units` : `Avg: ${fmtNum(metrics.avg_available_inv)} units`}
              icon={InventoryIcon} color="#8b5cf6" />
            <KPICard title="Shortage" value={String(kpiShort)}
              subtitle={isNodeView ? `${currentStats?.orders_shortage?.[0] || 0} unfulfilled orders` : `${metrics.shortage?.[0] || 0} unfulfilled orders`}
              icon={WarningAmberIcon} color="#f59e0b" />
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: `1px solid ${border}`, mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': { bgcolor: accent, height: 2, borderRadius: '2px 2px 0 0' },
              '& .MuiTab-root': {
                minHeight: 36, py: 0, px: 1.5, fontSize: '12px', fontWeight: 600, textTransform: 'none',
                color: textSecondary, '&.Mui-selected': { color: accent }, '&.Mui-disabled': { opacity: 0.3 },
              },
            }}>
              <Tab label="Charts" />
              <Tab label="Financial" />
              <Tab label="Supply & Demand" />
              <Tab label="Network" disabled={isNodeView} />
              <Tab label="Insights" disabled={isNodeView} />
              <Tab
                label={
                  <Stack direction="row" spacing={0.625} alignItems="center">
                    <TerminalIcon sx={{ fontSize: 13 }} />
                    <span>Sim Log</span>
                    {simulationLogs.length > 0 && (
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#10b981' }} />
                    )}
                  </Stack>
                }
              />
            </Tabs>
          </Box>

          {/* ── Tab 0: Inventory Charts ─── */}
          {tabValue === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isNodeView && selectedNodeId && chartData[selectedNodeId] ? (
                <ChartPanel nodeId={selectedNodeId} />
              ) : !isNodeView && Object.keys(chartData).length > 0 ? (
                Object.keys(chartData).map(nodeId => <ChartPanel key={nodeId} nodeId={nodeId} />)
              ) : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography sx={{ color: textSecondary, fontSize: '13px' }}>No inventory chart data</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 1: Financial ─── */}
          {tabValue === 1 && currentStats && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary }}>
                Financial Metrics{isNodeView ? ` — ${individual_stats[selectedNodeId]?.name}` : ''}
              </Typography>
              {isNodeView ? (
                <StatGrid items={[
                  { label: 'Profit',          value: fmtMoney(currentStats.profit, 0, sym),                  color: '#10b981' },
                  { label: 'Revenue',         value: fmtMoney(currentStats.revenue, 0, sym),                 color: '#3b82f6' },
                  { label: 'Node Cost',       value: fmtMoney(currentStats.node_cost, 2, sym),            color: '#ef4444' },
                  { label: 'Holding Cost',    value: fmtMoney(currentStats.inventory_carry_cost, 2, sym), color: '#8b5cf6' },
                  { label: 'Transport Cost',  value: fmtMoney(currentStats.transportation_cost, 2, sym),  color: '#f59e0b' },
                  { label: 'Inventory Spend', value: fmtMoney(currentStats.inventory_spend_cost, 2, sym), color: '#06b6d4' },
                ]} />
              ) : (
                <StatGrid items={[
                  { label: 'Net Profit',      value: fmtMoney(metrics.profit, 0, sym),                  color: '#10b981', sub: metrics.profit >= 0 ? 'positive' : 'negative' },
                  { label: 'Revenue',         value: fmtMoney(metrics.revenue, 0, sym),                 color: '#3b82f6' },
                  { label: 'Total Cost',      value: fmtMoney(metrics.total_cost, 2, sym),           color: '#ef4444' },
                  { label: 'Holding Cost',    value: fmtMoney(metrics.inventory_carry_cost, 2, sym), color: '#8b5cf6' },
                  { label: 'Transport Cost',  value: fmtMoney(metrics.transportation_cost, 2, sym),  color: '#f59e0b' },
                  { label: 'Inventory Spend', value: fmtMoney(metrics.inventory_spend_cost, 2, sym), color: '#06b6d4' },
                  { label: 'Avg / Order',     value: fmtMoney(metrics.avg_cost_per_order, 2, sym),   color: '#ec4899' },
                  { label: 'Avg / Item',      value: `${sym}${(metrics.avg_cost_per_item || 0).toFixed(4)}`, color: '#84cc16' },
                ]} />
              )}
            </Box>
          )}

          {/* ── Tab 2: Supply & Demand ─── */}
          {tabValue === 2 && currentStats && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary }}>
                Inventory & Demand{isNodeView ? ` — ${individual_stats[selectedNodeId]?.name}` : ''}
              </Typography>
              {isNodeView ? (
                <StatGrid items={[
                  { label: 'Inventory Level',      value: fmtNum(currentStats.inventory_level),                                                              color: '#10b981', sub: 'units on hand' },
                  { label: 'Inventory Waste',      value: fmtNum(currentStats.inventory_waste),                                                              color: '#ef4444', sub: 'units wasted' },
                  { label: 'Demand Placed',        value: fmtNum(Array.isArray(currentStats.demand_placed)       ? currentStats.demand_placed[1]       : currentStats.demand_placed),       color: '#8b5cf6', sub: Array.isArray(currentStats.demand_placed)       ? `${currentStats.demand_placed[0]} orders`       : '' },
                  { label: 'Fulfillment Received', value: fmtNum(Array.isArray(currentStats.fulfillment_received) ? currentStats.fulfillment_received[1] : currentStats.fulfillment_received), color: '#3b82f6', sub: Array.isArray(currentStats.fulfillment_received) ? `${currentStats.fulfillment_received[0]} orders` : '' },
                  { label: 'Demand Received',      value: fmtNum(Array.isArray(currentStats.demand_received)     ? currentStats.demand_received[1]     : currentStats.demand_received),     color: '#06b6d4', sub: Array.isArray(currentStats.demand_received)     ? `${currentStats.demand_received[0]} orders`     : '' },
                  { label: 'Demand Fulfilled',     value: fmtNum(Array.isArray(currentStats.demand_fulfilled)    ? currentStats.demand_fulfilled[1]    : currentStats.demand_fulfilled),    color: '#10b981', sub: Array.isArray(currentStats.demand_fulfilled)    ? `${currentStats.demand_fulfilled[0]} orders`    : '' },
                  { label: 'Orders Shortage',      value: fmtNum(Array.isArray(currentStats.orders_shortage)     ? currentStats.orders_shortage[1]     : currentStats.orders_shortage),     color: '#ef4444', sub: Array.isArray(currentStats.orders_shortage)     ? `${currentStats.orders_shortage[0]} unfulfilled` : '' },
                  { label: 'Backorder',            value: fmtNum(Array.isArray(currentStats.backorder)           ? currentStats.backorder[1]           : currentStats.backorder),           color: '#f59e0b', sub: Array.isArray(currentStats.backorder)           ? `${currentStats.backorder[0]} orders`           : '' },
                ]} />
              ) : (
                <StatGrid items={[
                  { label: 'Available Inventory', value: fmtNum(metrics.available_inv),          color: '#10b981', sub: 'units on hand' },
                  { label: 'Avg Inventory',       value: fmtNum(metrics.avg_available_inv),      color: '#3b82f6', sub: 'average units' },
                  { label: 'Total Demand',        value: fmtNum(metrics.total_demand?.[1] || 0), color: '#8b5cf6', sub: `${metrics.total_demand?.[0] || 0} orders` },
                  { label: 'Customer Demand',     value: fmtNum(metrics.demand_by_customers?.[1] || 0), color: '#f59e0b', sub: `${metrics.demand_by_customers?.[0] || 0} orders` },
                  { label: 'Shortage',            value: fmtNum(metrics.shortage?.[1] || 0),    color: '#ef4444', sub: `${metrics.shortage?.[0] || 0} unfulfilled orders` },
                  { label: 'Backorders',          value: fmtNum(metrics.backorders?.[1] || 0),  color: '#f97316', sub: `${metrics.backorders?.[0] || 0} orders pending` },
                  { label: 'Fulfillment Rate',    value: (() => { const d = metrics.total_demand?.[1] || 0; const s = metrics.shortage?.[1] || 0; return d > 0 ? `${Math.round(((d - s) / d) * 100)}%` : '—' })(), color: '#10b981', sub: 'demand satisfied' },
                  { label: 'Shortage Rate',       value: (() => { const d = metrics.total_demand?.[1] || 0; const s = metrics.shortage?.[1] || 0; return d > 0 ? `${Math.round((s / d) * 100)}%` : '0%' })(), color: '#dc2626', sub: 'demand unmet' },
                ]} />
              )}
            </Box>
          )}

          {/* ── Tab 3: Network ─── */}
          {tabValue === 3 && !isNodeView && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary }}>
                Network Configuration
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
                {[
                  { label: 'Total Nodes',   value: metrics.num_of_nodes    || 0, color: '#10b981', Icon: AccountTreeIcon  },
                  { label: 'Total Links',   value: metrics.num_of_links    || 0, color: '#3b82f6', Icon: LocalShippingIcon},
                  { label: 'Suppliers',     value: metrics.num_suppliers   || 0, color: '#10b981', Icon: WarehouseIcon    },
                  { label: 'Factories',     value: metrics.num_manufacturers || 0, color: '#f59e0b', Icon: FactoryIcon   },
                  { label: 'Distributors',  value: metrics.num_distributors || 0, color: '#3b82f6', Icon: LocalShippingIcon},
                  { label: 'Retailers',     value: metrics.num_retailers   || 0, color: '#8b5cf6', Icon: StorefrontIcon  },
                ].map(({ label, value, color, Icon: ItemIcon }) => (
                  <Box key={label} sx={{
                    p: 2, borderRadius: '12px', bgcolor: surfaceEl, border: `1px solid ${border}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                    transition: 'transform 0.15s ease',
                    '&:hover': { transform: 'translateY(-1px)' },
                  }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                      <ItemIcon sx={{ fontSize: 16, color }} />
                    </Box>
                    <Typography sx={{ fontSize: '26px', fontWeight: 900, color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                      {value}
                    </Typography>
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: textSecondary, textAlign: 'center' }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Tab 4: Insights ─── */}
          {tabValue === 4 && !isNodeView && (
            <InsightsPanel simulationResults={results} nodes={nodes} edges={edges} demands={demands} />
          )}

          {/* ── Tab 5: Simulation Log ─── */}
          {tabValue === 5 && (
            <SimLogViewer
              logs={simulationLogs}
              nodes={nodes}
              demands={demands}
              isDark={isDark}
              border={border}
              textSecondary={textSecondary}
              textPrimary={textPrimary}
              accent={accent}
              surfaceEl={surfaceEl}
            />
          )}

        </Box>
      </Box>
    </Box>
  )
}

export default SimulationDashboard
